import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import React, { useState } from "react";
import {
  FieldArray,
  FieldArrayPath,
  FieldArrayWithId,
  FieldValues,
  Path,
  UseFieldArrayPrepend,
  UseFieldArrayRemove,
} from "react-hook-form";
import { RhfControlledTableHeader } from "./RhfControlledTableHeader";
import { RhfControlledTableToolbar } from "./RhfControlledTableToolbar";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { RhfControlledTableColumnDefinition } from "./RhfControlledTableColumnDefinition";
import { MdmToolbarItem } from "./RhfControlledTableToolbarProps";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

type RhfControlledTableProps<
  TFormModel extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFormModel>
> = {
  fieldArrayName: TFieldArrayName;
  columns: RhfControlledTableColumnDefinition<TFormModel, TFieldArrayName>[];
  rows: FieldArrayWithId<TFormModel, TFieldArrayName>[];
  defaultForNewRow: FieldArray<TFormModel, TFieldArrayName>;
  additionalToolbarItems?: MdmToolbarItem[];
  insertRow: UseFieldArrayPrepend<TFormModel, TFieldArrayName>;
  removeRow: UseFieldArrayRemove;
};

export const RhfControlledDataTable = <
  TFormModel extends FieldValues,
  TFieldArrayName extends FieldArrayPath<TFormModel>
>({
  fieldArrayName,
  columns,
  rows,
  defaultForNewRow,
  additionalToolbarItems,
  insertRow,
  removeRow,
}: RhfControlledTableProps<TFormModel, TFieldArrayName>) => {
  const [selected, setSelected] = useState<string>("");

  const isSelected = (value: string) => selected === value;
  const getSelectedFieldIndex = (fieldId: string) => rows.findIndex((f) => f.id === fieldId);

  const handleAddItem = () => {
    insertRow(defaultForNewRow);
  };

  const handleDeleteItem = () => {
    const selectedIndex = getSelectedFieldIndex(selected);
    if (selectedIndex === -1) return;
    removeRow(selectedIndex);
    setSelected("");
  };

  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        sx={{
          width: "100%",
          mb: 2,
        }}
      >
        <RhfControlledTableToolbar
          itemSelected={selected !== ""}
          handleAddItem={handleAddItem}
          handleDeleteItem={handleDeleteItem}
          additionalItems={additionalToolbarItems}
          disableMutationButtons={!isUserInAdminNetworkRole}
        />
        <TableContainer sx={{ height: "43vh" }}>
          <Table sx={{ minWidth: 750 }} size="medium" stickyHeader>
            <RhfControlledTableHeader columns={columns} />
            <TableBody>
              {rows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <TableRow
                    hover
                    onClick={(_) => setSelected(row.id)}
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.fieldKey}>
                        {column.render(
                          `${fieldArrayName}.${index}.${column.fieldKey}` as Path<TFormModel>,
                          !isItemSelected,
                          index
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell align="center" colSpan={columns.length}>
                    No data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
